import { models, patients } from '@zipdrug/react-api-sdk';
import { alertOnError } from '@zipdrug/ui';
import { graphql } from '@apollo/client/react/hoc';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import withRefreshPatients from '../../../hoc/withRefreshPatients';
import LanguageMenu from './LanguageMenu';

const LanguageMenuContainer = ({
  mutateUpdatePatient,
  onLanguageUpdated,
  onRefetchPatients,
  patient = {},
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [displayLanguage, setDisplayLanguage] = useState(patient?.language ?? null);
  const languageOptions = Object.keys(models.LanguageType);

  useEffect(() => {
    setDisplayLanguage(patient?.language ?? null);
  }, [patient?.language]);

  const onToggleEdit = useCallback(() => {
    setIsEditing(editing => {
      const nextEditing = !editing;
      if (!nextEditing) {
        setSelectedLanguage(null);
      }
      return nextEditing;
    });
  }, []);
  const onSelectLanguage = useCallback(language => setSelectedLanguage(language), []);

  const onSubmit = useCallback(async () => {
    if (!patient?.id || !selectedLanguage) return;

    const previousLanguage = patient?.language ?? null;
    const nextLanguage = models.LanguageType[selectedLanguage];

    setDisplayLanguage(nextLanguage);
    if (onLanguageUpdated) onLanguageUpdated(nextLanguage);

    const result = await alertOnError(
      mutateUpdatePatient({
        variables: {
          id: patient.id,
          modifier: {
            language: nextLanguage,
          },
        },
      }),
    );

    if (!result) {
      setDisplayLanguage(previousLanguage);
      if (onLanguageUpdated) onLanguageUpdated(previousLanguage);
      return;
    }

    onRefetchPatients();
    setSelectedLanguage(null);
    setIsEditing(false);
  }, [mutateUpdatePatient, onLanguageUpdated, onRefetchPatients, patient, selectedLanguage]);

  const patientWithOptimisticLanguage = useMemo(
    () => ({
      ...patient,
      language: displayLanguage ?? patient?.language,
    }),
    [displayLanguage, patient],
  );

  return (
    <LanguageMenu
      {...props}
      isEditing={isEditing}
      onToggleEdit={onToggleEdit}
      selectedLanguage={selectedLanguage}
      onSelectLanguage={onSelectLanguage}
      onSubmit={onSubmit}
      languageOptions={languageOptions}
      patient={patientWithOptimisticLanguage}
    />
  );
};

LanguageMenuContainer.propTypes = {
  mutateUpdatePatient: PropTypes.func,
  onLanguageUpdated: PropTypes.func,
  onRefetchPatients: PropTypes.func,
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    language: PropTypes.string,
  }),
};

export default graphql(patients.mutateUpdatePatient, { name: 'mutateUpdatePatient' })(
  withRefreshPatients(LanguageMenuContainer),
);
